import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import StylePackItem from './style-pack-item';
import StylePackRequestBtn from './style-pack-request-btn';
import { StylePackContentType, StylePackData, StylePackGroup, StylePackItemTypes, templateLabels, templateSubLabels } from '../types/stylepacks';
import ButtonClose from '../general-components/button-close';
import { stylePackBannerImages } from '../../utils/content-templates';
import styled from 'styled-components';
import StylePackThemeOptions from './style-pack-theme-options';
import ButtonBack from '../general-components/button-back';

const BannerContainer = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	width: 100%;
	z-index: -1;
`;

const BannerImage = styled.img`
	width: 100%;
`;

const BannerGradient = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(180deg, rgba(38, 36, 43, 0.45) -60.83%, rgba(38, 36, 43, 0.69) 38.59%, rgba(38, 36, 43, 0.93) 67.15%, var(--primitives-mode-1-gray-900) 98.03%);
`;

export interface StylePacksInfoProps {
	stylePack?: StylePackData;
	selectedItem: string;
	goBack: () => void;
	closeStylePackTab: () => void;
	doRender?: boolean;
}

export interface InfoMap {
	[key: string]: string;
}

const backupStylePack: StylePackData = {
	id: 'backup',
	name: 'Miscellaneous',
	locked: false,
	stylePack: [],
};

const displayTitlesFirst = (a:StylePackGroup, b:StylePackGroup) => {
	if (a.type === 'title') return -1;
	else if (b.type === 'title') return 1;
	else return 0;
}

export default function StylePackInfo(props: StylePacksInfoProps) {	
	const {
		stylePack,
		selectedItem = '',
		goBack,
		closeStylePackTab
	} = props;

	const name = stylePack ? stylePack.name : backupStylePack.name;
	const locked = stylePack ? stylePack.locked : backupStylePack.locked;
	const stylePackContent = stylePack ? stylePack.stylePack : backupStylePack.stylePack;
	const id = stylePack ? stylePack.id : backupStylePack.id;
	const strict = stylePack ? stylePack.strict : false;

	const [showThemeOptions, setShowThemeOptions] = useState<boolean>(false);
	const [containerWidth, setContainerWidth] = useState<number>(0);

	const infoRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (infoRef.current) {
			setContainerWidth(infoRef.current.getBoundingClientRect().width);
		}
	}, [infoRef.current]);

	useEffect(() => {
		if (selectedItem) {
			setTimeout(() => {
				const item = document.getElementById(`style-pack-info-content-item-${selectedItem}`);
				if (item) {
					item.scrollIntoView({behavior: 'smooth', block: 'end', inline: 'nearest'});
				}
			}, 500);
		}
	}, [selectedItem]);

	const isMiscellaneous = useMemo(() => id === 'miscellaneous', [id]);

	const bannerImage = stylePackBannerImages[id];

	function handleStylepackClose () {
		goBack();
		closeStylePackTab();
	}

	if (!stylePack?.id) {
		return null;
	}

	function getSkeletonSize(contentType: StylePackContentType) {
		const isTitle = contentType === StylePackContentType.TITLE;
		const parentWidth = containerWidth - (isTitle ? 16 : 8);
		const targetWidth = isTitle ? parentWidth / 3 : parentWidth / 2;
		const targetHeight = targetWidth * 9 / 16;
		return { width: targetWidth, height: targetHeight, style: { top: '-3px' } };
	}

	return (<div
		className={classNames('style-pack-info', { 
			'has-request-button': locked,
		})}
		data-testid="style-pack-info"
	>
		<div className="style-pack-info-content" data-testid="style-pack-info-content" ref={infoRef}>
			{bannerImage && (
				<BannerContainer className='banner-container'>
					<BannerImage src={bannerImage} />
					<BannerGradient />
				</BannerContainer>
			)}
			<div className="style-pack-info-content-header info-header" data-strict={strict}>
				<div className="info-header-left">
					<ButtonBack onClick={goBack}></ButtonBack>
					<h4 className="bl-font-header margin-none">{name}</h4>
				</div>
				<div className="info-header-right">
					{!strict && (<div 
						className="theme-options-dropdown-button"
						onMouseEnter={() => setShowThemeOptions(true)}
						onMouseLeave={() => setShowThemeOptions(false)}
					>
						<Icon name={ICONS.CUSTOM_THEME} color={COLORS.WHITE} size={16} />
					</div>)}
					<StylePackThemeOptions stylepack={name} showThemeOptions={showThemeOptions} />
					<ButtonClose onClick={handleStylepackClose} noPadding />
				</div>
			</div>
			{strict && (<div className='style-pack-info-content-info'>
				<Icon name={ICONS.CROWN} color={COLORS.WHITE} size={12} />
				<p className='bl-font-p2' style={{margin: 0}}>Want to customize this stylepack?</p>
				<div style={{flexGrow: 1}}></div>
				<StylePackRequestBtn
					stylePackName={name}
					buttonText="Contact BLS"
					containerClassName="inline-request-container"
					buttonClassName="request-button"
				/>
			</div>)}
			{stylePackContent.sort(displayTitlesFirst).map((pack, index) => (
				<div key={`style-pack-info-${index}`}>
					{pack.templates.length > 0 && (
						<>
							<div
								className="style-pack-info-content-title"
								data-testid={`style-pack-info-content-title-${index}`}
								key={`style-pack-info-content-title-${index}`}
							>
								<span>{templateLabels[pack.type]}</span>
								{templateSubLabels[pack.type] !== '' && (<span className='style-pack-info-content-subtitle'>{templateSubLabels[pack.type]}</span>)}
							</div>
							<div
								className={classNames('style-pack-info-content-items', { misc: isMiscellaneous, title: pack.type === 'title' })}
								data-testid={`style-pack-info-content-items-${index}`}
								key={`style-pack-info-content-items-${index}`}
							>
								{pack.templates.map((item, _index) => (
									<React.Fragment key={`style-pack-info-item-wrapper-${item.id}-${_index}`}>
										<StylePackItem
											key={`style-pack-info-item-${item.id}-${_index}`}
											item={item}
											type={StylePackItemTypes.Info}
											skeletonSize={getSkeletonSize(item.contentType ?? StylePackContentType.SEGMENT)}
										/>
									</React.Fragment>
								))}
							</div>
						</>
					)}
				</div>
			))}
		</div>
	</div>);
}
